import {
  Activity,
  Bell,
  BookOpen,
  ClipboardList,
  FileQuestion,
  FileSearch,
  Grid2X2,
  HeartPulse,
  Home,
  MessageCircle,
  Network,
  ScrollText,
  Shield,
  ShieldCheck,
  Stethoscope,
  Tag,
  UserCog,
  Users,
  FileText
} from 'lucide-react';

import type { NavigationConfigGroup } from '@/shared/config/navigation.types';

export const adminNavigationGroups: readonly NavigationConfigGroup[] = [
  {
    labelKey: '',
    items: [
      { labelKey: 'navigation.home', path: '/home', icon: Home },
    ],
  },
  {
    labelKey: 'navigationGroups.patientMedicalStaff',
    items: [
      {
        labelKey: 'navigation.patientManagement',
        path: '/patient-management',
        icon: Users,
        // PRD 01 §1.2: PATIENT:NO_PERMISSION users get LNB hidden + entry blocked.
        permission: { category: 'PATIENT', level: 'READ' },
      },
      {
        labelKey: 'navigation.medicalStaffManagement',
        path: '/medical-staff-management',
        icon: UserCog,
        // PRD 03 §1.2: STAFF:NO_PERMISSION users get LNB hidden + entry blocked.
        permission: { category: 'STAFF', level: 'READ' },
      },
    ],
  },
  {
    labelKey: 'navigationGroups.proManagement',
    items: [
      // PRD 04 §1.2: PRO_MGMT:NO_PERMISSION users get LNB hidden + entry blocked.
      {
        labelKey: 'navigation.proItemDesign',
        path: '/pro-item-design',
        icon: Network,
        permission: { category: 'PRO_MGMT', level: 'READ' },
      },
      // PRD 05 §1.2: PRO_MGMT:NO_PERMISSION users get LNB hidden + entry blocked.
      {
        labelKey: 'navigation.questionnaireDesign',
        path: '/questionnaire-design',
        icon: FileText,
        permission: { category: 'PRO_MGMT', level: 'READ' },
      },
      // PRD 06 §1.2: PRO_MGMT:NO_PERMISSION users get LNB hidden + entry blocked.
      {
        labelKey: 'navigation.proDeploymentManagement',
        path: '/pro-deployment-management',
        icon: Grid2X2,
        permission: { category: 'PRO_MGMT', level: 'READ' },
      },
    ],
  },
  {
    labelKey: 'navigationGroups.metadataManagement',
    items: [
      {
        labelKey: 'navigation.managementGuidelines',
        path: '/management-guidelines',
        icon: ShieldCheck,
        // PRD 07 §1.2: METADATA:NO_PERMISSION users have the LNB item hidden +
        // direct-URL entry blocked. READ users see the menu; the page itself
        // also defends with a NoPermissionGate for belt-and-braces.
        permission: { category: 'METADATA', level: 'READ' },
      },
      {
        labelKey: 'navigation.diseaseCode',
        path: '/disease-code',
        icon: Tag,
        // PRD 08 §1.2: METADATA:NO_PERMISSION users have the LNB item hidden +
        // direct-URL entry blocked. READ users see the menu; the page itself
        // also defends with a NoPermissionGate for belt-and-braces.
        permission: { category: 'METADATA', level: 'READ' },
      },
      {
        labelKey: 'navigation.biometricData',
        path: '/biometric-data',
        icon: HeartPulse,
        // PRD 09 §1.2: METADATA:NO_PERMISSION users have the LNB item hidden +
        // direct-URL entry blocked. READ users see the menu; the page itself
        // also defends with a NoPermissionGate for belt-and-braces.
        permission: { category: 'METADATA', level: 'READ' },
      },
    ],
  },
  {
    labelKey: 'navigationGroups.contentManagement',
    items: [
      {
        labelKey: 'navigation.healthEncyclopedia',
        path: '/health-encyclopedia',
        icon: BookOpen,
        // PRD 10 §1.2: CONTENT_LIB:NO_PERMISSION users (seeds OPERATION, SYSTEM)
        // have the LNB item hidden + direct-URL entry blocked. READ/WRITE/Root
        // see the menu; the page also defends with a NoPermissionGate.
        permission: { category: 'CONTENT_LIB', level: 'READ' },
      },
      {
        labelKey: 'navigation.counselingManual',
        path: '/counseling-manual',
        icon: ClipboardList,
        // PRD 11 §1.2: CONTENT_LIB:NO_PERMISSION users (seeds OPERATION,
        // SYSTEM) have the LNB item hidden + direct-URL entry blocked. READ
        // users see the menu; the page also defends with a NoPermissionGate.
        permission: { category: 'CONTENT_LIB', level: 'READ' },
      },
    ],
  },
  {
    labelKey: 'navigationGroups.customerServiceCenter',
    items: [
      {
        labelKey: 'navigation.patientInquiryManagement',
        path: '/patient-inquiry-management',
        icon: MessageCircle,
        // PRD 12 §1.2: the LNB item is SHOWN to all roles (per the PRD 21 §4.5 /
        // 22 §2.2.2 global rule — see AdminLayout). CUSTOMER_SUPPORT:NO_PERMISSION
        // (seeds CONTENT, SYSTEM) are blocked on entry via useRequirePermission,
        // NOT by hiding the menu. The `permission` tag drives that per-page check.
        permission: { category: 'CUSTOMER_SUPPORT', level: 'READ' },
      },
      {
        labelKey: 'navigation.noticeManagement',
        path: '/notice-management',
        icon: Bell,
        // PRD 13 §1.2 + PRD 21 §4.5: the LNB item stays visible for ALL roles
        // (incl. PUBLICATION:NO_PERMISSION seeds OPERATION, SYSTEM). Entry is
        // blocked per-page via useRequirePermission; this tag drives that gate,
        // not LNB hiding.
        permission: { category: 'PUBLICATION', level: 'READ' },
      },
      {
        labelKey: 'navigation.termsAndConditionsManagement',
        path: '/terms-and-conditions-management',
        icon: ScrollText,
        // PRD 14 §1.2 + PRD 21 §4.5: the LNB item stays visible for ALL roles
        // (incl. PUBLICATION:NO_PERMISSION seeds OPERATION, SYSTEM). Entry is
        // blocked per-page via useRequirePermission; this tag drives that gate,
        // not LNB hiding.
        permission: { category: 'PUBLICATION', level: 'READ' },
      },
    ],
  },
  {
    labelKey: 'navigationGroups.systemManagement',
    items: [
      {
        labelKey: 'navigation.adminManagement',
        path: '/admin-management',
        icon: Shield,
        // PRD 15 §1.2: SYSTEM_MGMT:NO_PERMISSION users get the LNB item hidden.
        permission: { category: 'SYSTEM_MGMT', level: 'READ' },
      },
      {
        labelKey: 'navigation.roleManagement',
        path: '/role-management',
        icon: ShieldCheck,
        // PRD 16 §1.2: SYSTEM_MGMT:NO_PERMISSION users get the LNB item hidden.
        permission: { category: 'SYSTEM_MGMT', level: 'READ' },
      },
      {
        labelKey: 'navigation.notificationSettings',
        path: '/notification-settings',
        icon: Bell,
        // PRD 17 §1.2: the LNB item stays EXPOSED for SYSTEM_MGMT:NO_PERMISSION users;
        // entry is blocked on the page by useRequirePermission (LNB 노출, 진입 시 차단).
        permission: { category: 'SYSTEM_MGMT', level: 'READ' },
      },
      {
        labelKey: 'navigation.logInquiry',
        path: '/log-inquiry',
        icon: FileSearch,
        // PRD 18 §1.2: SYSTEM_MGMT:NO_PERMISSION users (seeds OPERATION,
        // CONTENT) have the LNB item hidden + direct-URL entry blocked. READ
        // users see the menu; the page also defends with a NoPermissionGate.
        permission: { category: 'SYSTEM_MGMT', level: 'READ' },
      },
    ],
  },
];

export const adminPlaceholderPaths = adminNavigationGroups
  .flatMap((group) => group.items)
  .map((item) => item.path)
  .filter((path) => path !== '/home' && path !== '/role-management');
